import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import HistoryIcon from "@mui/icons-material/History";
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Paper,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { CodeListFileDto } from "Infrastructure/Api/Api";
import { useState } from "react";
import { useDeleteCodeListFileMutation } from "../../Api/Mutations/useDeleteCodeListFileMutation";

type Props = {
	files: CodeListFileDto[];
	selectedFolder: string | null;
	onViewVersionHistory: (file: CodeListFileDto) => void;
	isLoading?: boolean;
};

function formatFileSize(bytes: number): string {
	if (bytes === 0) return "0 B";
	const k = 1024;
	const sizes = ["B", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

function formatDate(dateString: string): string {
	return new Date(dateString).toLocaleString("cs-CZ", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export const FileList: React.FC<Props> = ({
	files,
	selectedFolder,
	onViewVersionHistory,
	isLoading,
}) => {
	const [copiedId, setCopiedId] = useState<number | null>(null);
	const [deleteDialogFile, setDeleteDialogFile] =
		useState<CodeListFileDto | null>(null);

	const deleteMutation = useDeleteCodeListFileMutation();

	const filteredFiles = selectedFolder
		? files.filter((f) => f.folderPath === selectedFolder && f.isActive)
		: [];

	const handleDownload = (file: CodeListFileDto) => {
		if (file.downloadUrl) {
			const link = document.createElement("a");
			link.href = file.downloadUrl;
			link.download = file.fileName;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	};

	const handleCopyUrl = async (file: CodeListFileDto) => {
		if (file.downloadUrl) {
			await navigator.clipboard.writeText(file.downloadUrl);
			setCopiedId(file.codeListFileID);
			setTimeout(() => setCopiedId(null), 1500);
		}
	};

	const handleDeleteConfirm = () => {
		if (deleteDialogFile) {
			deleteMutation.mutate(
				{
					folderPath: deleteDialogFile.folderPath,
					fileName: deleteDialogFile.fileName,
				},
				{
					onSettled: () => setDeleteDialogFile(null),
				},
			);
		}
	};

	if (isLoading) {
		return (
			<Box sx={{ p: 2, flex: 1 }}>
				<Skeleton height={40} sx={{ mb: 1 }} />
				{[1, 2, 3, 4, 5].map((i) => (
					<Skeleton key={i} height={52} sx={{ mb: 0.5 }} />
				))}
			</Box>
		);
	}

	if (!selectedFolder) {
		return (
			<Box
				sx={{
					flex: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					p: 4,
				}}
			>
				<Typography color="text.secondary">
					Vyberte složku pro zobrazení souborů
				</Typography>
			</Box>
		);
	}

	if (filteredFiles.length === 0) {
		return (
			<Box
				sx={{
					flex: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					p: 4,
				}}
			>
				<Typography color="text.secondary">
					Ve složce "{selectedFolder}" nejsou žádné soubory
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
			<TableContainer component={Paper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: 600 }}>Soubor</TableCell>
							<TableCell sx={{ fontWeight: 600 }}>Verze</TableCell>
							<TableCell sx={{ fontWeight: 600 }}>Velikost</TableCell>
							<TableCell sx={{ fontWeight: 600 }}>Nahráno</TableCell>
							<TableCell sx={{ fontWeight: 600 }}>Nahrál</TableCell>
							<TableCell sx={{ fontWeight: 600 }} align="right">
								Akce
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{filteredFiles.map((file) => (
							<TableRow
								key={file.codeListFileID}
								sx={{
									"&:hover": {
										backgroundColor: "action.hover",
									},
								}}
							>
								<TableCell>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Typography variant="body2">{file.fileName}</Typography>
										{file.isActive && (
											<Chip
												size="small"
												icon={<CheckCircleIcon />}
												label="Aktivní"
												color="success"
												variant="outlined"
												sx={{ height: 22 }}
											/>
										)}
									</Box>
								</TableCell>
								<TableCell>
									<Chip
										size="small"
										label={`v${file.version}`}
										variant="outlined"
										sx={{ height: 22 }}
									/>
								</TableCell>
								<TableCell>{formatFileSize(file.size)}</TableCell>
								<TableCell>{formatDate(file.uploadedAt)}</TableCell>
								<TableCell>{file.uploadedByName || file.uploadedBy}</TableCell>
								<TableCell align="right">
									<Tooltip title="Historie verzí">
										<IconButton
											size="small"
											onClick={() => onViewVersionHistory(file)}
										>
											<HistoryIcon fontSize="small" />
										</IconButton>
									</Tooltip>
									<Tooltip title="Stáhnout">
										<IconButton
											size="small"
											onClick={() => handleDownload(file)}
											disabled={!file.downloadUrl}
										>
											<DownloadIcon fontSize="small" />
										</IconButton>
									</Tooltip>
									<Tooltip
										title={
											copiedId === file.codeListFileID
												? "Zkopírováno"
												: "Kopírovat URL"
										}
									>
										<IconButton
											size="small"
											onClick={() => handleCopyUrl(file)}
											disabled={!file.downloadUrl}
										>
											<ContentCopyIcon fontSize="small" />
										</IconButton>
									</Tooltip>
									<Tooltip title="Smazat">
										<IconButton
											size="small"
											onClick={() => setDeleteDialogFile(file)}
											color="error"
										>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			<Dialog
				open={!!deleteDialogFile}
				onClose={() => setDeleteDialogFile(null)}
			>
				<DialogTitle>Smazat soubor</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Opravdu chcete smazat soubor "{deleteDialogFile?.fileName}" a
						všechny jeho verze? Tato akce je nevratná.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						variant="text"
						onClick={() => setDeleteDialogFile(null)}
						disabled={deleteMutation.isPending}
					>
						Zrušit
					</Button>
					<Button
						variant="text"
						color="error"
						onClick={handleDeleteConfirm}
						disabled={deleteMutation.isPending}
					>
						Smazat
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
